import { ComponentPropsWithoutRef, ReactNode } from "react";
import {
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormHandleSubmit,
} from "react-hook-form";

export interface FormProps<TFieldValues extends FieldValues>
  extends Omit<
    ComponentPropsWithoutRef<"form">,
    "onSubmit" | "onInvalid" | "children" | "noValidate"
  > {
  /** `handleSubmit` from the caller's own `useForm<TFieldValues>()`. */
  handleSubmit: UseFormHandleSubmit<TFieldValues>;
  /** Called with typed, already-validated values. */
  onSubmit: SubmitHandler<TFieldValues>;
  /** Called instead of `onSubmit` when validation fails. Rarely needed —
   * `TextField`/`SelectField` already surface each field's own error — but
   * left available for a form-level reaction (e.g. focusing the first
   * invalid field, logging a submit attempt). */
  onInvalid?: SubmitErrorHandler<TFieldValues>;
  /** Not defaulted to `true`: a form that only picks options (nothing
   * `type="email"`/`pattern`/`required` on a native input can enforce
   * beyond what Yup already validates) has nothing for the browser to
   * intercept, so forcing it on every consumer would be a no-op most of
   * the time and a surprise the one time a caller *does* want native
   * validation. */
  noValidate?: boolean;
  children: ReactNode;
}

/**
 * The `<form>` every Carbon screen submits through: wires `handleSubmit`
 * from the caller's own `useForm()` (React Hook Form + Yup stays — this
 * only gives it a shared shape) so `onSubmit` only ever runs with values
 * that already passed validation.
 *
 * Deliberately thin: it owns nothing about layout or fields. `TextField`/
 * `SelectField` bind individual Carbon inputs to the same `control`;
 * everything else (a `Stack`, a `FormGroup`, a submit `Button`) is the
 * consumer's own markup inside `children`.
 *
 * Owned by the primitives layer — Tasks 11/12 (create/edit user) and,
 * after them, Sites/Tags/Materials consume this verbatim. If a flow needs
 * different submit behaviour, it changes here, not in a fork.
 */
function Form<TFieldValues extends FieldValues>({
  handleSubmit,
  onSubmit,
  onInvalid,
  noValidate = false,
  children,
  ...rest
}: FormProps<TFieldValues>) {
  return (
    <form
      noValidate={noValidate}
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      {...rest}
    >
      {children}
    </form>
  );
}

export default Form;
